/**
 * Flutter側から実行するNuxt側の関数をグローバルに登録する
 */
import { useUpdateChecker } from '@/composables/useUpdateChecker';
import { useMapState } from '@/composables/store/useMapStore';
import { PushRepository } from '@/features/push/api/pushRepository';
import { Location, CanGpsAccess } from '@/config/gps';
import { _Window } from '@/types/window';
import { UserRepository } from '~/features/user/api/userRepository';

export default defineNuxtPlugin(async () => {
  const router = useRouter();
  const repository = PushRepository();
  const { setCanGpsAccess, setCurrentLocation } = useMapState();
  const { checkUpdate } = useUpdateChecker();
  const { checkGPSPermission } = useGPSPermissionChecker();
  const { logEvent } = useFlutterConnection();
  const { registerDeviceInfo } = UserRepository();

  /** Nuxt側でRouterバックする */
  const FGoBack = () => {
    console.log('go back from flutter');
    router.back();
  };

  /** Nuxt側で現在のページをリロードする */
  const FReloadPage = async () => {
    console.log(`reload page from flutter`);
    reloadNuxtApp();
  };

  /** Nuxt側で任意のページへ遷移する */
  const FToThePage = async (path: string) => {
    console.log(`go to the Page:${path} from flutter`);

    // パスの先頭にスラッシュがない場合は追加
    // Flutter から "parking/451265?garageId=..." のような形式で来る場合に対応
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;

    // パスとクエリパラメータを分離
    const [pathname, queryString] = normalizedPath.split('?');

    if (queryString) {
      // クエリパラメータがある場合は、パスとクエリを分けて渡す
      const query: Record<string, string> = {};
      const params = new URLSearchParams(queryString);
      params.forEach((value, key) => {
        query[key] = value;
      });
      router.push({ path: pathname, query });
    } else {
      // クエリパラメータがない場合はそのまま
      router.push(normalizedPath);
    }
  };

  /** アプリ再開時に実行する処理 */
  const FCheckUpdate = async () => {
    console.log('FCheckUpdate from flutter');
    await Promise.all([
      // アップデートが必要かチェック
      checkUpdate(),
      // 座標取得ができるかチェック
      checkGPSPermission(),
    ]);
  };

  /** Push通知開封時に実行する処理 */
  const FOpenPushNotification = async (pushId: string) => {
    console.log(`FOpenPushNotification from flutter: ${pushId}`);
    await repository.open(Number(pushId));
  };

  /** GPSの取得可否をグローバルストアに保存 */
  const FSetCanGpsAccess = (_canGpsAccess: CanGpsAccess) => {
    console.log(
      'canGpsAccess from flutter',
      `canAccess: ${_canGpsAccess.canAccess}, errorCode: ${_canGpsAccess.errorCode}`,
    );
    setCanGpsAccess(_canGpsAccess);
  };

  /** 現在の座標情報をグローバルストアに保存 */
  const FSetCurrentLocation = (_location: Location) => {
    console.log(
      'currentLocation from flutter',
      `lat: ${_location.lat}, lng: ${_location.lng}, head: ${_location.heading}`,
    );
    setCurrentLocation({
      lat: _location.lat,
      lng: _location.lng,
      heading:
        _location.heading && _location.heading > 0 ? _location.heading : 0,
    });
  };

  /** FCMトークンの更新リクエスト（アプリがバックグラウンド上で定期的にFCMトークンを更新するため） */
  const FRequestGetFCMToken = () => {
    console.log('FRequestGetFCMToken from flutter');
    registerDeviceInfo();
  };

  window.FGoBack = FGoBack;
  window.FReloadPage = FReloadPage;
  window.FToThePage = FToThePage;
  window.FCheckUpdate = FCheckUpdate;
  window.FOpenPushNotification = FOpenPushNotification;
  window.FSetCanGpsAccess = FSetCanGpsAccess;
  window.FSetCurrentLocation = FSetCurrentLocation;
  window.logEvent = logEvent;
  window.FRequestGetFCMToken = FRequestGetFCMToken;
});

declare const window: _Window;
